import React, { useRef, useEffect, useState } from "react";

const IntroBlock = () => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
      }
    };

    updateCanvasSize();
    window.addEventListener("resize", updateCanvasSize);

    return () => {
      window.removeEventListener("resize", updateCanvasSize);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || dimensions.width === 0 || dimensions.height === 0) return;

    // Set canvas size to match container
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const numLines = 140;
    const lineSegments = 200;
    const baseLineAlpha = 0.8;
    const baseLineWidth = 0.8;
    const morphSpeed = 0.0008;
    const baseRotateSpeed = 0.0003;
    let time = 2000;

    // Enhanced forms with a new fourth form
    const forms = [
      // Original complex spiral form
      (u, v, t) => {
        const theta = u * Math.PI * 2;
        const phi = v * Math.PI;
        let r = 120 + 30 * Math.sin(phi * 4 + theta * 2);
        r += 20 * Math.sin(phi * 6) * Math.cos(theta * 3);
        let x = r * Math.sin(phi) * Math.cos(theta);
        let y = r * Math.sin(phi) * Math.sin(theta);
        let z = r * Math.cos(phi) + 20 * Math.sin(theta * 5 + phi * 3);
        return { x, y, z };
      },
      // Enhanced oval form with breathing
      (u, v, t) => {
        const theta = u * Math.PI * 2;
        const phi = v * Math.PI;
        const breathe = 1 + 0.1 * Math.sin(t * 0.01);
        let r = (150 + 20 * Math.cos(phi * 8)) * breathe;
        r *= 0.8 + 0.2 * Math.abs(Math.cos(theta * 2));
        let x = r * Math.sin(phi) * Math.cos(theta);
        let y = r * Math.sin(phi) * Math.sin(theta);
        let z = r * Math.cos(phi) * (0.8 + 0.3 * Math.sin(theta * 4));
        return { x, y, z };
      },
      // Enhanced hollow form
      (u, v, t) => {
        const theta = u * Math.PI * 2;
        const phi = v * Math.PI;
        let r = 120;
        r += 50 * Math.sin(phi * 3) * Math.sin(theta * 2.5);
        r += 30 * Math.cos(phi * 5 + theta);
        let x = r * Math.sin(phi) * Math.cos(theta);
        let y = r * Math.sin(phi) * Math.sin(theta);
        let z = r * Math.cos(phi);
        const hollow = Math.max(0, Math.sin(phi * 2 + theta * 3) - 0.7);
        r *= 1 - hollow * 0.8;
        return { x, y, z };
      },
      // NEW: Crystalline flower form
      (u, v, t) => {
        const theta = u * Math.PI * 2;
        const phi = v * Math.PI;
        const petals = 6;
        const petalMod = Math.abs(Math.sin((theta * petals) / 2));
        let r = 100 + 60 * petalMod * Math.sin(phi * 2);
        r += 25 * Math.cos(phi * 8 + theta * 3);
        const twist = phi * 0.5 + Math.sin(theta * 4) * 0.3;
        let x = r * Math.sin(phi) * Math.cos(theta + twist);
        let y = r * Math.sin(phi) * Math.sin(theta + twist);
        let z =
          r * Math.cos(phi) + 15 * Math.sin(theta * petals) * Math.sin(phi * 3);
        return { x, y, z };
      },
    ];

    const interpolateForms = (formA, formB, u, v, t, blend) => {
      const pointA = formA(u, v, t);
      const pointB = formB(u, v, t);
      return {
        x: pointA.x * (1 - blend) + pointB.x * blend,
        y: pointA.y * (1 - blend) + pointB.y * blend,
        z: pointA.z * (1 - blend) + pointB.z * blend,
      };
    };

    const getCurrentForm = (u, v, t) => {
      const totalForms = forms.length;
      const cycleTime = 800;
      const position = (t % (cycleTime * totalForms)) / cycleTime;
      const formIndex = Math.floor(position);
      const nextFormIndex = (formIndex + 1) % totalForms;
      let rawBlend = position - formIndex;
      const pauseTime = 0.1;
      const transitionTime = 1 - pauseTime * 2;
      let blend;

      if (rawBlend < pauseTime) {
        blend = 0;
      } else if (rawBlend > 1 - pauseTime) {
        blend = 1;
      } else {
        const normalizedTime = (rawBlend - pauseTime) / transitionTime;
        // Enhanced easing with elastic feel
        blend =
          normalizedTime < 0.5
            ? 4 * normalizedTime * normalizedTime * normalizedTime
            : 1 - Math.pow(-2 * normalizedTime + 2, 3) / 2;
      }

      return interpolateForms(
        forms[formIndex],
        forms[nextFormIndex],
        u,
        v,
        t,
        blend
      );
    };

    // Dynamic color calculation using pastel primary colors
    const getLineColor = (v, t, alpha) => {
      const colorPhase = (t * 0.005) % (Math.PI * 2);
      const formPhase = (t % (800 * forms.length)) / (800 * forms.length);

      // Primary colors: Red (0°), Yellow (60°), Blue (240°)
      const primaryHues = [0, 60, 240];
      const baseHueIndex = Math.floor((formPhase * 3 + v * 2) % 3);
      const baseHue = primaryHues[baseHueIndex];

      // Add some variation while staying close to primary colors
      const hueVariation = Math.sin(colorPhase + v * Math.PI) * 15;
      const hue = (baseHue + hueVariation + 360) % 360;

      // Pastel characteristics: moderate saturation, high lightness
      const saturation = 45 + Math.sin(colorPhase + v * 3) * 15; // 30-60%
      const lightness = 75 + Math.sin(t * 0.003 + v * Math.PI) * 10; // 65-85%

      return `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha})`;
    };

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      // Black background with subtle variation
      const gradient = ctx.createRadialGradient(
        width / 2,
        height / 2,
        0,
        width / 2,
        height / 2,
        width / 2
      );
      const bgPhase = Math.sin(time * 0.002) * 0.02;
      gradient.addColorStop(0, `hsl(0, 0%, ${8 + bgPhase * 5}%)`);
      gradient.addColorStop(1, `hsl(0, 0%, ${3 + bgPhase * 5}%)`);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // Mouse influence on rotation
      const mouseInfluenceX = (mousePos.x / width - 0.5) * 0.5;
      const mouseInfluenceY = (mousePos.y / height - 0.5) * 0.5;

      const rotateSpeed = baseRotateSpeed * (1 + Math.sin(time * 0.001) * 0.3);
      const rotateX = Math.sin(time * rotateSpeed) * 0.6 + mouseInfluenceY;
      const rotateY =
        Math.cos(time * rotateSpeed * 0.7) * 0.4 + mouseInfluenceX;
      const rotateZ = time * rotateSpeed * 0.15;

      // Primary wireframe layer
      for (let i = 0; i < numLines; i++) {
        const v = i / (numLines - 1);
        ctx.beginPath();

        const lineAlpha =
          baseLineAlpha * (0.8 + 0.2 * Math.sin(time * 0.01 + v * Math.PI));
        ctx.strokeStyle = getLineColor(v, time, lineAlpha);
        ctx.lineWidth =
          baseLineWidth * (0.9 + 0.1 * Math.sin(time * 0.008 + v * 5));

        let lastPointVisible = false;
        let lastPoint = null;

        for (let j = 0; j <= lineSegments; j++) {
          const u = j / lineSegments;
          const point = getCurrentForm(u, v, time);

          // Enhanced 3D rotation
          let rotatedX =
            point.x * Math.cos(rotateZ) - point.y * Math.sin(rotateZ);
          let rotatedY =
            point.x * Math.sin(rotateZ) + point.y * Math.cos(rotateZ);
          let rotatedZ = point.z;

          // Apply Y rotation
          const tempX = rotatedX;
          rotatedX = tempX * Math.cos(rotateY) + rotatedZ * Math.sin(rotateY);
          rotatedZ = -tempX * Math.sin(rotateY) + rotatedZ * Math.cos(rotateY);

          const scale = 1.8 + rotatedZ * 0.0015;
          const projX = width / 2 + rotatedX * scale;
          const projY = height / 2 + rotatedY * scale;
          const pointVisible = rotatedZ > -100;

          if (j === 0) {
            if (pointVisible) {
              ctx.moveTo(projX, projY);
              lastPointVisible = true;
              lastPoint = { x: projX, y: projY };
            }
          } else {
            if (pointVisible && lastPointVisible) {
              ctx.lineTo(projX, projY);
            } else if (pointVisible && !lastPointVisible) {
              ctx.moveTo(projX, projY);
            }
          }
          lastPointVisible = pointVisible;
          lastPoint = { x: projX, y: projY };
        }
        ctx.stroke();
      }

      // Secondary cross-hatching layer with different opacity
      for (let i = 0; i < numLines * 0.4; i++) {
        const u = i / (numLines * 0.4 - 1);
        ctx.beginPath();

        const lineAlpha =
          baseLineAlpha *
          0.6 *
          (0.6 + 0.4 * Math.sin(time * 0.012 + u * Math.PI));
        ctx.strokeStyle = getLineColor(u + 0.5, time + 200, lineAlpha);
        ctx.lineWidth = baseLineWidth * 0.6;

        let lastPointVisible = false;
        let lastPoint = null;

        for (let j = 0; j <= lineSegments * 0.6; j++) {
          const v = j / (lineSegments * 0.6);
          const point = getCurrentForm(u, v, time);

          let rotatedX =
            point.x * Math.cos(rotateZ) - point.y * Math.sin(rotateZ);
          let rotatedY =
            point.x * Math.sin(rotateZ) + point.y * Math.cos(rotateZ);
          let rotatedZ = point.z;

          const tempX = rotatedX;
          rotatedX = tempX * Math.cos(rotateY) + rotatedZ * Math.sin(rotateY);
          rotatedZ = -tempX * Math.sin(rotateY) + rotatedZ * Math.cos(rotateY);

          const scale = 1.8 + rotatedZ * 0.0015;
          const projX = width / 2 + rotatedX * scale;
          const projY = height / 2 + rotatedY * scale;
          const pointVisible = rotatedZ > -100;

          if (j === 0) {
            if (pointVisible) {
              ctx.moveTo(projX, projY);
              lastPointVisible = true;
              lastPoint = { x: projX, y: projY };
            }
          } else {
            if (pointVisible && lastPointVisible) {
              ctx.lineTo(projX, projY);
            } else if (pointVisible && !lastPointVisible) {
              ctx.moveTo(projX, projY);
            }
          }
          lastPointVisible = pointVisible;
          lastPoint = { x: projX, y: projY };
        }
        ctx.stroke();
      }

      time += 0.6;
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Mouse tracking for interactive rotation
    const handleMouseMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      setMousePos({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
    };

    canvas.addEventListener("mousemove", handleMouseMove);
    animate();

    return () => {
      canvas.removeEventListener("mousemove", handleMouseMove);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (canvas && ctx) {
        ctx.clearRect(0, 0, width, height);
      }
    };
  }, [mousePos, dimensions]);

  return (
    <div className="intro-blob-container" ref={containerRef}>
      <canvas
        ref={canvasRef}
        className="intro-blob-canvas"
        style={{
          filter: "contrast(1.05) brightness(1.02)",
          width: "100%",
          height: "100%",
        }}
      />
    </div>
  );
};

export default IntroBlock;
